import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type {
  ApiKey,
  CreateApiKeyData,
  UpdateApiKeyData,
  CreateApiKeyResponse,
  ApiKeyUsageStats,
  ApiKeyRotateResponse,
  ApiKeyRole,
  CreateApiKeyRoleData,
  PermissionCheckRequest,
  PermissionCheckResponse,
  ApiKeyFilters,
  ApiKeyPagination,
  DetailedUsageStats,
  BulkUsageStats,
  ExpirationData,
  PermissionCategoriesResponse,
  RateLimitInfo,
  RateLimitUpdate,
  ApiDocumentation,
  ExportFormat,
  ApiKeyListResponse,
} from '@/features/api-keys/types';

/**
 * API client for API Key management
 * Updated to match the new backend API specification
 */
export const apiKeysApi = {
  // === Basic API Key Operations ===

  // List all API keys for an organization
  listApiKeys: async (
    orgId: string,
    filters?: ApiKeyFilters,
    pagination?: Partial<ApiKeyPagination>
  ): Promise<ApiKey[] | ApiKeyListResponse> => {
    try {
      const params = new URLSearchParams();

      // Add pagination parameters
      if (pagination?.page) {
        params.append('page', pagination.page.toString());
      }
      if (pagination?.per_page) {
        params.append('per_page', pagination.per_page.toString());
      }

      // Add filter parameters
      if (filters) {
        if (filters.key_type) {
          params.append('key_type', filters.key_type);
        }
        if (filters.created_after) {
          params.append('created_after', filters.created_after);
        }
        if (filters.created_before) {
          params.append('created_before', filters.created_before);
        }
      }

      const queryString = params.toString();
      const url = queryString
        ? `${API_ENDPOINTS.API_KEYS.LIST(orgId)}?${queryString}`
        : API_ENDPOINTS.API_KEYS.LIST(orgId);

      const response = await apiClient.get(url);
      return response.data;
    } catch (error: unknown) {
      // If backend returns 404 for no keys, return empty array
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { status: number } };
        if (axiosError.response.status === 404) {
          return [];
        }
      }
      throw error;
    }
  },

  // Create a new API key for an organization
  createApiKey: async (orgId: string, keyData: CreateApiKeyData): Promise<CreateApiKeyResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.API_KEYS.CREATE(orgId), keyData);
    return response.data;
  },

  // Get a specific API key by ID
  getApiKey: async (orgId: string, keyId: string): Promise<ApiKey> => {
    const response = await apiClient.get(API_ENDPOINTS.API_KEYS.BY_ID(orgId, keyId));
    return response.data;
  },

  // Update an existing API key
  updateApiKey: async (
    orgId: string,
    keyId: string,
    updateData: UpdateApiKeyData
  ): Promise<ApiKey> => {
    const response = await apiClient.patch(API_ENDPOINTS.API_KEYS.UPDATE(orgId, keyId), updateData);
    return response.data;
  },

  // Delete/revoke an API key
  deleteApiKey: async (orgId: string, keyId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.API_KEYS.DELETE(orgId, keyId));
  },

  // Rotate an API key
  rotateApiKey: async (orgId: string, keyId: string): Promise<ApiKeyRotateResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.API_KEYS.ROTATE(orgId, keyId));
    return response.data;
  },

  // Get usage statistics for an API key
  getApiKeyUsageStats: async (
    orgId: string,
    keyId: string,
    days: number = 30
  ): Promise<ApiKeyUsageStats> => {
    const params = new URLSearchParams({ days: days.toString() });
    const response = await apiClient.get(
      `${API_ENDPOINTS.API_KEYS.USAGE_STATS(orgId, keyId)}?${params}`
    );
    return response.data;
  },

  // === Enhanced API Key Operations ===

  // === Role Management ===

  // List API key roles
  listApiKeyRoles: async (orgId: string): Promise<ApiKeyRole[]> => {
    const response = await apiClient.get(API_ENDPOINTS.API_KEYS.ROLES.LIST(orgId));
    return response.data;
  },

  // Create API key role (admin only)
  createApiKeyRole: async (orgId: string, roleData: CreateApiKeyRoleData): Promise<ApiKeyRole> => {
    const data = { ...roleData, organization_id: orgId };
    const response = await apiClient.post(API_ENDPOINTS.API_KEYS.ROLES.CREATE(orgId), data);
    return response.data;
  },

  // === Permission Check ===

  // Check permission for current API key
  checkPermission: async (
    orgId: string,
    request: PermissionCheckRequest
  ): Promise<PermissionCheckResponse> => {
    const data = { ...request, organization_id: orgId };
    const response = await apiClient.post(API_ENDPOINTS.API_KEYS.CHECK_PERMISSION(orgId), data);
    return response.data;
  },

  // Phase 5.4: Enhanced usage statistics
  getDetailedUsage: async (
    orgId: string,
    keyId: string,
    period: string = '30d',
    granularity: string = 'day',
    includeGeography: boolean = false
  ): Promise<DetailedUsageStats> => {
    const params = new URLSearchParams({
      period,
      granularity,
      include_geography: includeGeography.toString(),
    });
    const response = await apiClient.get(
      `${API_ENDPOINTS.API_KEYS.BY_ID(orgId, keyId)}/usage/detailed?${params}`
    );
    return response.data;
  },

  // Get bulk usage statistics across all keys
  getBulkUsageStats: async (orgId: string): Promise<BulkUsageStats> => {
    const response = await apiClient.get(`${API_ENDPOINTS.API_KEYS.LIST(orgId)}/usage/bulk`);
    return response.data;
  },

  // Phase 5.4: Expiration management
  getExpirationData: async (orgId: string): Promise<ExpirationData> => {
    const response = await apiClient.get(`${API_ENDPOINTS.API_KEYS.LIST(orgId)}/expiration`);
    return response.data;
  },

  // Extend expiry for single or multiple keys
  extendExpiry: async (orgId: string, keyIds: string[], expiresAt?: string): Promise<ApiKey[]> => {
    const response = await apiClient.post(`${API_ENDPOINTS.API_KEYS.LIST(orgId)}/extend-expiry`, {
      organization_id: orgId,
      key_ids: keyIds,
      expires_at: expiresAt,
    });
    return response.data;
  },

  // Permission catalog (categories, all permissions, role/scope mappings)
  getPermissionCategories: async (orgId: string): Promise<PermissionCategoriesResponse> => {
    const response = await apiClient.get(
      API_ENDPOINTS.API_KEYS.PERMISSION_CATEGORIES(orgId)
    );
    return response.data;
  },

  // Update permissions for a key
  updatePermissions: async (
    orgId: string,
    keyId: string,
    permissions: string[]
  ): Promise<ApiKey> => {
    const response = await apiClient.patch(
      API_ENDPOINTS.API_KEYS.PERMISSIONS(orgId, keyId),
      {
        permissions,
      }
    );
    return response.data;
  },

  // Phase 5.4: Rate limiting
  getRateLimit: async (orgId: string, keyId: string): Promise<RateLimitInfo> => {
    const response = await apiClient.get(
      `${API_ENDPOINTS.API_KEYS.BY_ID(orgId, keyId)}/rate-limit`
    );
    return response.data;
  },

  // Update rate limits
  updateRateLimit: async (
    orgId: string,
    keyId: string,
    limits: RateLimitUpdate
  ): Promise<RateLimitInfo> => {
    const response = await apiClient.patch(
      `${API_ENDPOINTS.API_KEYS.BY_ID(orgId, keyId)}/rate-limit`,
      {
        organization_id: orgId,
        ...limits,
      }
    );
    return response.data;
  },

  // Phase 5.4: API Documentation
  getApiDocumentation: async (): Promise<ApiDocumentation> => {
    const response = await apiClient.get('/docs/api-reference');
    return response.data;
  },

  // Export usage data
  exportUsageData: async (
    orgId: string,
    keyId: string,
    format: ExportFormat = 'csv',
    period: string = '30d'
  ): Promise<Blob> => {
    const params = new URLSearchParams({ format, period });
    const response = await apiClient.get(
      `${API_ENDPOINTS.API_KEYS.BY_ID(orgId, keyId)}/usage/export?${params}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Batch operations
  batchDeleteApiKeys: async (orgId: string, keyIds: string[]): Promise<void> => {
    await apiClient.post(`${API_ENDPOINTS.API_KEYS.LIST(orgId)}/batch-delete`, {
      organization_id: orgId,
      key_ids: keyIds,
    });
  },

  // Batch update API keys (e.g., enable/disable multiple keys)
  batchUpdateApiKeys: async (
    orgId: string,
    keyIds: string[],
    updateData: Partial<UpdateApiKeyData>
  ): Promise<ApiKey[]> => {
    const response = await apiClient.patch(`${API_ENDPOINTS.API_KEYS.LIST(orgId)}/batch-update`, {
      organization_id: orgId,
      key_ids: keyIds,
      ...updateData,
    });
    return response.data;
  },

  // Test an API key (check if it's valid and active)
  testApiKey: async (
    orgId: string,
    keyId: string
  ): Promise<{ valid: boolean; message: string }> => {
    const response = await apiClient.post(`${API_ENDPOINTS.API_KEYS.BY_ID(orgId, keyId)}/test`, {
      organization_id: orgId,
    });
    return response.data;
  },

  // Get available permissions for API keys
  getAvailablePermissions: async (
    orgId: string
  ): Promise<Array<{ id: string; name: string; description: string; category: string }>> => {
    const response = await apiClient.get(`${API_ENDPOINTS.API_KEYS.LIST(orgId)}/permissions`);
    return response.data;
  },

  // Legacy compatibility methods (maintain backwards compatibility)
  getApiKeyUsage: async (orgId: string, keyId: string): Promise<ApiKeyUsageStats> => {
    return apiKeysApi.getApiKeyUsageStats(orgId, keyId);
  },

  // Regenerate an API key (legacy - use rotateApiKey instead)
  regenerateApiKey: async (orgId: string, keyId: string): Promise<ApiKeyRotateResponse> => {
    return apiKeysApi.rotateApiKey(orgId, keyId);
  },
};

// Export individual functions for convenience
export const {
  listApiKeys,
  createApiKey,
  getApiKey,
  updateApiKey,
  deleteApiKey,
  rotateApiKey,
  getApiKeyUsageStats,
  listApiKeyRoles,
  createApiKeyRole,
  checkPermission,
  batchDeleteApiKeys,
  batchUpdateApiKeys,
  testApiKey,
  getAvailablePermissions,
  getApiKeyUsage, // Legacy compatibility
  regenerateApiKey, // Legacy compatibility
} = apiKeysApi;
